import { useState, useCallback, useRef, useEffect } from 'react';
import moment from 'moment';
import { openSnackbarMain } from 'components/Shared/openSnackbarMain';
import { getListaRemessas } from '../../../../api/remessas';

const FILTROS_LABELS = {
  codigoRemessaDe: 'Cód. Remessa De',
  codigoRemessaAte: 'Cód. Remessa Até',
  remessaBancoDe: 'Nº Rem. Banco De',
  remessaBancoAte: 'Nº Rem. Banco Até',
  operacao: 'Operação',
  banco: 'Banco',
  codigoTitulo: 'Cód. Título',
  nomeSacado: 'Nome Sacado',
  nomeCedente: 'Nome Cedente',
  cnpjSacado: 'CNPJ Sacado',
  cnpjCedente: 'CNPJ Cedente',
  vencimentoDe: 'Vencimento De',
  vencimentoAte: 'Vencimento Até',
  entradaDe: 'Entrada De',
  entradaAte: 'Entrada Até',
  incluidoDe: 'Incluído De',
  incluidoAte: 'Incluído Até',
  valorDe: 'Valor De',
  valorAte: 'Valor Até',
  documento: 'Documento',
  notaFiscal: 'Nº Nota Fiscal',
  tipoDocumento: 'Tipo Documento',
  empresa: 'Empresa',
  carteira: 'Carteira',
  ocorrenciaBancaria: 'Ocorrência Bancária',
  remessasBaixaCustodia: 'Remessas Baixa/Custódia'
};

const MAPEAMENTO_FILTROS = {
  codigoRemessaDe: 'cod_remessa_inicial',
  codigoRemessaAte: 'cod_remessa_final',
  remessaBancoDe: 'numero_remessa_inicial',
  remessaBancoAte: 'numero_remessa_final',
  operacao: 'cod_operacao',
  banco: 'cod_banco',
  codigoTitulo: 'cod_titulo',
  nomeSacado: 'sacado',
  nomeCedente: 'cedente',
  cnpjSacado: 'cpfcnpj_sacado',
  cnpjCedente: 'cpfcnpj_cedente',
  documento: 'documento',
  tipoDocumento: 'tipo_documento',
  carteira: 'carteira',
  ocorrenciaBancaria: 'ocorrencia',
  empresa: 'cod_empresa',
  entradaDe: 'entrada_inicial',
  entradaAte: 'entrada_final',
  vencimentoDe: 'vencimento_inicial',
  vencimentoAte: 'vencimento_final',
  incluidoDe: 'data_inclusao_inicial',
  incluidoAte: 'data_inclusao_final',
  valorDe: 'valor_inicial',
  valorAte: 'valor_final',
  notaFiscal: 'nota'
};

const ORDEM_PRIORIDADE_CHIPS = ['remessaBancoDe', 'remessaBancoAte', 'operacao', 'documento'];
const CAMPOS_CNPJ = ['cnpjSacado', 'cnpjCedente'];
const CAMPOS_VALOR = ['valorDe', 'valorAte'];
const CAMPOS_DATA_DE = ['vencimentoDe', 'entradaDe'];
const CAMPOS_DATA = [
  'entradaDe',
  'entradaAte',
  'vencimentoDe',
  'vencimentoAte',
  'incluidoDe',
  'incluidoAte'
];

const formatDateUS = value =>
  value ? moment(value, ['DD/MM/YYYY', moment.ISO_8601], true).format('YYYY-MM-DD') : null;

const formatarDataChip = value => {
  const dataFormatada = moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);
  return dataFormatada.isValid() ? dataFormatada.format('DD/MM/YYYY') : value;
};

const possuiValorPreenchido = valor => valor !== '' && valor !== null && valor !== undefined;

const normalizarValorParaBackend = valor => {
  const textoValor = String(valor || '')
    .replace(/\s/g, '')
    .replace(/^R\$/i, '')
    .trim();
  if (!textoValor) return '';

  if (/^\d+$/.test(textoValor)) return textoValor;

  return textoValor.replace(/\./g, '').replace(',', '.');
};

const buildFiltrosPayload = (filtros, paginationModel) => {
  const params = {
    page: paginationModel.page + 1,
    per_page: paginationModel.pageSize,
    tipo_remessa: 2
  };

  if (!filtros) return params;

  Object.entries(filtros).forEach(([chave, valor]) => {
    if (chave === 'remessasBaixaCustodia') {
      if (valor === true) params.tipo_remessa = 1;
      return;
    }

    const chaveBackend = MAPEAMENTO_FILTROS[chave];
    if (!chaveBackend || valor === '' || valor === null || valor === undefined) return;

    const ehCampoData = CAMPOS_DATA.includes(chave);
    if (ehCampoData) {
      params[chaveBackend] = formatDateUS(valor);
      return;
    }

    if (CAMPOS_CNPJ.includes(chave)) {
      params[chaveBackend] = String(valor).replace(/\D/g, '');
      return;
    }

    if (CAMPOS_VALOR.includes(chave)) {
      params[chaveBackend] = normalizarValorParaBackend(valor);
      return;
    }

    params[chaveBackend] = valor;
  });

  return params;
};

const useRemessasCriadasHook = () => {
  const [drawerAberto, setDrawerAberto] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [filtrosAplicados, setFiltrosAplicados] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const filtrosRef = useRef(null);
  const totalRef = useRef(null);

  useEffect(() => {
    carregarRemessas({ page: 0, pageSize: 10 }, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirDrawer = useCallback(() => setDrawerAberto(true), []);
  const fecharDrawer = useCallback(() => setDrawerAberto(false), []);
  const abrirModal = useCallback(() => setModalAberto(true), []);
  const fecharModal = useCallback(() => setModalAberto(false), []);

  const carregarRemessas = useCallback((model, filtros) => {
    const params = buildFiltrosPayload(filtros, model);

    if (totalRef.current !== null) {
      params.total = totalRef.current;
    }

    setCarregando(true);

    getListaRemessas(params)
      .then(response => {
        const { meta } = response.data;
        setResultados(response.data.data ?? []);
        setTotalRegistros(meta?.total ?? 0);
        totalRef.current = meta?.total ?? null;
      })
      .catch(() => {
        openSnackbarMain('Ocorreu um erro ao buscar as remessas', 'error');
      })
      .finally(() => setCarregando(false));
  }, []);

  const aplicarFiltros = useCallback(
    filtros => {
      const incluidoDeFoiInformado = possuiValorPreenchido(filtros?.incluidoDe);
      const possuiOutroFiltroDataDe = CAMPOS_DATA_DE.some(chave =>
        possuiValorPreenchido(filtros?.[chave])
      );

      if (!incluidoDeFoiInformado && !possuiOutroFiltroDataDe) {
        openSnackbarMain(
          'Ao remover o filtro "Incluído — De", informe pelo menos outro filtro "Data — De".',
          'warning'
        );
        return;
      }

      setFiltrosAplicados(filtros);
      filtrosRef.current = filtros;
      totalRef.current = null;
      setDrawerAberto(false);
      const novoModel = { page: 0, pageSize: paginationModel.pageSize };
      setPaginationModel(novoModel);
      carregarRemessas(novoModel, filtros);
    },
    [paginationModel.pageSize, carregarRemessas]
  );

  const limparFiltros = useCallback(() => {
    setFiltrosAplicados(null);
    filtrosRef.current = null;
    totalRef.current = null;
    const novoModel = { page: 0, pageSize: 10 };
    setPaginationModel(novoModel);
    setSortModel([]);
    carregarRemessas(novoModel, null);
  }, [carregarRemessas]);

  const handlePaginationModelChange = useCallback(
    newModel => {
      setPaginationModel(newModel);
      carregarRemessas(newModel, filtrosRef.current);
    },
    [carregarRemessas]
  );

  const handleSortModelChange = useCallback(newSortModel => {
    setSortModel(newSortModel);
  }, []);

  const recarregarListaAtual = useCallback(() => {
    carregarRemessas(paginationModel, filtrosRef.current);
  }, [carregarRemessas, paginationModel]);

  const obterChipsFiltros = useCallback(() => {
    if (!filtrosAplicados) return [];

    const chipsTodos = Object.entries(filtrosAplicados)
      .filter(([chave, valor]) => {
        if (chave === 'remessasBaixaCustodia') return valor === true;
        return valor !== '' && valor !== null && valor !== undefined;
      })
      .map(([chave, valor]) => {
        let valorExibicao = valor;
        if (chave === 'remessasBaixaCustodia') valorExibicao = 'Sim';
        if (CAMPOS_DATA.includes(chave)) {
          valorExibicao = formatarDataChip(valor);
        }
        return {
          chave,
          label: FILTROS_LABELS[chave] || chave,
          valor: String(valorExibicao)
        };
      });

    const chipsPrioritarios = [];
    const chipsDemais = [];

    chipsTodos.forEach(chip => {
      if (ORDEM_PRIORIDADE_CHIPS.includes(chip.chave)) {
        chipsPrioritarios.push(chip);
      } else {
        chipsDemais.push(chip);
      }
    });

    chipsPrioritarios.sort(
      (chipA, chipB) =>
        ORDEM_PRIORIDADE_CHIPS.indexOf(chipA.chave) - ORDEM_PRIORIDADE_CHIPS.indexOf(chipB.chave)
    );

    return [...chipsPrioritarios, ...chipsDemais];
  }, [filtrosAplicados]);

  return {
    drawerAberto,
    modalAberto,
    filtrosAplicados,
    resultados,
    carregando,
    totalRegistros,
    paginationModel,
    sortModel,
    abrirDrawer,
    fecharDrawer,
    abrirModal,
    fecharModal,
    aplicarFiltros,
    limparFiltros,
    recarregarListaAtual,
    handlePaginationModelChange,
    handleSortModelChange,
    obterChipsFiltros
  };
};

export default useRemessasCriadasHook;
